/** Opaque frost/char response on the original weighted leg surfaces. The source
 * geometry, UVs and textures stay shared and unchanged. Four uniform channels
 * follow domain-owned contact/fracture ages; there is no independent clock,
 * extra draw call, particle mesh or transparent shell. */
export function installSkyCinderFrost(T, mesh, materials) {
  if (!mesh.isSkinnedMesh || !mesh.skeleton) return;
  const names = mesh.skeleton.bones.map((b) => b.name);
  const limbs = Array.from({ length: 4 }, (_, i) =>
    names.flatMap((name, j) => (new RegExp(`^leg_0${i}_[0-2]$`).test(name) ? [j] : [])),
  );
  if (limbs.every((indices) => !indices.length)) return;
  const masks = limbs.map((indices) =>
    indices.length ? indices.map((i) => `(1.0-step(0.25,abs(bone-${i}.0)))`).join('+') : '0.0',
  );
  const declaration = `varying vec4 vCinderLeg; varying vec3 vCinderSurface;
    vec4 cinderMask(float bone) { return vec4(${masks.join(',')}); }`;
  const clone = (source) => {
    const material = source.clone(),
      prior = source.onBeforeCompile,
      key = source.customProgramCacheKey();
    const uniforms = {
      cinderFrost: { value: new T.Vector4() },
      cinderFracture: { value: new T.Vector4() },
    };
    material.userData.cinderUniforms = uniforms;
    material.onBeforeCompile = function (shader, renderer) {
      prior.call(this, shader, renderer);
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', `#include <common>\n${declaration}`)
        .replace(
          '#include <skinbase_vertex>',
          `#include <skinbase_vertex>
        vCinderLeg=cinderMask(skinIndex.x)*skinWeight.x+cinderMask(skinIndex.y)*skinWeight.y+cinderMask(skinIndex.z)*skinWeight.z+cinderMask(skinIndex.w)*skinWeight.w;
        vCinderSurface=position;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
        varying vec4 vCinderLeg; varying vec3 vCinderSurface;
        uniform vec4 cinderFrost; uniform vec4 cinderFracture;`,
        )
        .replace(
          '#include <color_fragment>',
          `#include <color_fragment>
        float frost=clamp(dot(vCinderLeg,cinderFrost),0.0,1.0);
        float fracture=clamp(dot(vCinderLeg,cinderFracture),0.0,1.0);
        float grain=0.7+0.3*sin(dot(vCinderSurface,vec3(23.1,31.7,19.3)));
        diffuseColor.rgb=mix(diffuseColor.rgb,vec3(0.40,0.68,0.73)*grain,frost*0.85*(1.0-fracture*0.7));
        diffuseColor.rgb=mix(diffuseColor.rgb,diffuseColor.rgb*0.22,fracture*0.85);`,
        )
        .replace(
          '#include <roughnessmap_fragment>',
          `#include <roughnessmap_fragment>
        roughnessFactor=mix(roughnessFactor,0.95,max(frost,fracture));`,
        );
    };
    material.customProgramCacheKey = () => `${key}:cinder-frost-1:${masks.join(';')}`;
    materials.add(material);
    return material;
  };
  mesh.material = Array.isArray(mesh.material) ? mesh.material.map(clone) : clone(mesh.material);
}

/** Project only finite canonical simulation state into private material uniforms.
 * Pause/reduced-motion never advances a material animation or conceals injury. */
export function updateSkyCinderFrost(group, item) {
  const safe = (values) =>
    Array.from({ length: 4 }, (_, i) =>
      Number.isFinite(values?.[i]) ? Math.max(0, Math.min(1, values[i])) : 0,
    );
  for (const material of group.userData.authoredMaterials ?? []) {
    const uniforms = material.userData.cinderUniforms;
    if (!uniforms) continue;
    uniforms.cinderFrost.value.fromArray(safe(item.cinderheart?.frost));
    uniforms.cinderFracture.value.fromArray(safe(item.cinderheart?.fracture));
  }
}
